<template>
  <div class="discount-rule-preview__overlay" @click.self="$emit('close')">
    <div class="discount-rule-preview">
      <div class="discount-rule-preview__header">
        <h2 class="discount-rule-preview__title">
          <i class="fa fa-eye"></i>
          Rule Preview
        </h2>
        <button class="discount-rule-preview__close" @click="$emit('close')">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="discount-rule-preview__content">
        <!-- Basic Info -->
        <div class="discount-rule-preview__section">
          <h3 class="discount-rule-preview__section-title">
            <i class="fa fa-info-circle"></i> Basic Info
          </h3>
          <div class="discount-rule-preview__info-grid">
            <div class="discount-rule-preview__info-item">
              <span class="discount-rule-preview__label">Name</span>
              <span class="discount-rule-preview__value">{{ rule.name || 'Untitled' }}</span>
            </div>
            <div class="discount-rule-preview__info-item">
              <span class="discount-rule-preview__label">Type</span>
              <span class="discount-rule-preview__value discount-rule-preview__badge">
                {{ formatDiscountType(rule.discountType) }}
              </span>
            </div>
            <div class="discount-rule-preview__info-item">
              <span class="discount-rule-preview__label">Status</span>
              <span 
                class="discount-rule-preview__value discount-rule-preview__badge"
                :class="rule.isActive ? 'discount-rule-preview__badge--success' : 'discount-rule-preview__badge--danger'"
              >
                {{ rule.isActive ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="discount-rule-preview__info-item">
              <span class="discount-rule-preview__label">Priority</span>
              <span class="discount-rule-preview__value">{{ rule.priority || 0 }}</span>
            </div>
          </div>
          <div v-if="rule.description" class="discount-rule-preview__description">
            <span class="discount-rule-preview__label">Description</span>
            <p>{{ rule.description }}</p>
          </div>
        </div>

        <!-- Bundle Groups Summary (for bundle type) -->
        <div v-if="hasBundleGroups" class="discount-rule-preview__section">
          <h3 class="discount-rule-preview__section-title">
            <i class="fa fa-box"></i> Bundle Groups
          </h3>
          <div class="discount-rule-preview__bundle-groups">
            <div 
              v-for="(group, index) in rule.bundleGroups" 
              :key="'bundle-group-' + index"
              class="discount-rule-preview__bundle-group"
            >
              <div class="discount-rule-preview__bundle-group-header">
                <span class="discount-rule-preview__bundle-group-label">
                  {{ group.label || `Group ${index + 1}` }}
                </span>
                <span class="discount-rule-preview__bundle-group-qty">
                  {{ group.requiredQuantity }} required
                </span>
              </div>
              <p class="discount-rule-preview__summary">
                {{ formatConditions(group.conditions) }}
              </p>
            </div>
          </div>
          <div v-if="rule.usageLimits && rule.usageLimits.maxPerOrder > 0" class="discount-rule-preview__max-bundles">
            Max bundles per order: <strong>{{ rule.usageLimits.maxPerOrder }}</strong>
          </div>
          <div v-else class="discount-rule-preview__max-bundles">
            <em>Unlimited bundles per order</em>
          </div>
        </div>

        <!-- Conditions Summary (for non-bundle types) -->
        <div v-else class="discount-rule-preview__section">
          <h3 class="discount-rule-preview__section-title">
            <i class="fa fa-filter"></i> Conditions
          </h3>
          <div v-if="hasConditions" class="discount-rule-preview__conditions">
            <p class="discount-rule-preview__summary">
              {{ formatConditions(rule.conditions) }}
            </p>
          </div>
          <div v-else class="discount-rule-preview__empty">
            Applies to all items (no conditions)
          </div>
        </div>

        <!-- Triggers Summary -->
        <div class="discount-rule-preview__section">
          <h3 class="discount-rule-preview__section-title">
            <i class="fa fa-bolt"></i> Triggers
          </h3>
          <div class="discount-rule-preview__triggers">
            <ul v-if="hasTriggers">
              <li v-if="rule.triggers.minQuantity > 0">
                Minimum <strong>{{ rule.triggers.minQuantity }}</strong> matching items in cart
              </li>
              <li v-if="rule.triggers.minSpend > 0">
                Minimum spend of <strong>${{ rule.triggers.minSpend.toFixed(2) }}</strong>
              </li>
              <li v-if="rule.triggers.minCannabisGrams > 0">
                Minimum <strong>{{ rule.triggers.minCannabisGrams }}g</strong> cannabis
              </li>
              <li v-if="hasCustomerConditions">
                Customer conditions: {{ formatConditions(rule.triggers.customerConditions) }}
              </li>
            </ul>
            <div v-else class="discount-rule-preview__empty">
              No triggers required (applies automatically)
            </div>
          </div>
        </div>

        <!-- Effects Summary -->
        <div class="discount-rule-preview__section">
          <h3 class="discount-rule-preview__section-title">
            <i class="fa fa-magic"></i> Effects
          </h3>
          <div v-if="rule.effects && rule.effects.length > 0" class="discount-rule-preview__effects">
            <div 
              v-for="(effect, index) in rule.effects" 
              :key="index"
              class="discount-rule-preview__effect-card"
            >
              <span class="discount-rule-preview__effect-type">
                {{ formatEffectType(effect.type) }}
              </span>
              <span class="discount-rule-preview__effect-value">
                {{ formatEffectValue(effect) }}
              </span>
              <div class="discount-rule-preview__effect-details">
                <span v-if="effect.applyTo !== 'all'">
                  Apply to: {{ formatApplyTo(effect.applyTo) }}
                </span>
                <span v-if="effect.maxQuantity > 0">
                  Max: {{ effect.maxQuantity }} items
                </span>
              </div>
            </div>
          </div>
          <div v-else class="discount-rule-preview__empty">
            No effects defined
          </div>
        </div>

        <!-- Source Types -->
        <div v-if="rule.sourceType && rule.sourceType.length > 0" class="discount-rule-preview__section">
          <h3 class="discount-rule-preview__section-title">
            <i class="fa fa-desktop"></i> Source Types
          </h3>
          <div class="discount-rule-preview__tags">
            <span 
              v-for="source in rule.sourceType" 
              :key="source"
              class="discount-rule-preview__tag"
            >
              {{ formatSourceType(source) }}
            </span>
          </div>
        </div>

        <!-- Stacking -->
        <div v-if="rule.stacking" class="discount-rule-preview__section">
          <h3 class="discount-rule-preview__section-title">
            <i class="fa fa-layer-group"></i> Stacking
          </h3>
          <p>{{ formatStackingBehavior(rule.stacking.stackingBehavior) }}</p>
        </div>
      </div>

      <div class="discount-rule-preview__footer">
        <button class="discount-rule-preview__btn" @click="$emit('close')">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DiscountRulePreview",
  props: {
    rule: {
      type: Object,
      required: true,
    },
  },
  computed: {
    hasConditions() {
      return this.rule.conditions?.rules?.length > 0;
    },
    hasBundleGroups() {
      return this.rule.discountType === 'bundle' && this.rule.bundleGroups?.length > 0;
    },
    hasTriggers() {
      return (
        (this.rule.triggers?.minQuantity > 0) ||
        (this.rule.triggers?.minSpend > 0) ||
        (this.rule.triggers?.minCannabisGrams > 0) ||
        this.hasCustomerConditions
      );
    },
    hasCustomerConditions() {
      return this.rule.triggers?.customerConditions?.rules?.length > 0;
    },
  },
  methods: {
    formatDiscountType(type) {
      const types = {
        instant: "Instant",
        coupon: "Coupon",
        salePrice: "Sale Price",
        specialPricing: "Special Pricing",
        customer: "Customer",
        loyalty: "Loyalty",
        bundle: "Bundle",
      };
      return types[type] || type;
    },
    formatEffectType(type) {
      const types = {
        PERCENT_OFF: "Percent Off",
        DOLLAR_OFF: "Dollar Off",
        FIXED_PRICE: "Fixed Price",
        FREE: "Free",
      };
      return types[type] || type;
    },
    formatEffectValue(effect) {
      switch (effect.type) {
        case "PERCENT_OFF":
          return `${effect.value}%`;
        case "DOLLAR_OFF":
          return `$${effect.value?.toFixed(2) || '0.00'}`;
        case "FIXED_PRICE":
          return `$${effect.value?.toFixed(2) || '0.00'}`;
        case "FREE":
          return "Free";
        default:
          return effect.value;
      }
    },
    formatApplyTo(applyTo) {
      const options = {
        all: "All Items",
        cheapest: "Cheapest First",
        most_expensive: "Most Expensive First",
      };
      return options[applyTo] || applyTo;
    },
    formatSourceType(source) {
      const sources = {
        pos: "POS",
        "online-store": "Online Store",
        kiosk: "Kiosk",
      };
      return sources[source] || source;
    },
    formatStackingBehavior(behavior) {
      const behaviors = {
        default: "Uses store default stacking rules",
        stackWithAll: "Can stack with all other discounts",
        exclusive: "Exclusive - cannot stack with other discounts",
        custom: "Custom stacking rules",
      };
      return behaviors[behavior] || behavior;
    },
    formatConditions(conditions) {
      if (!conditions?.rules?.length) return "None";
      
      const formatRule = (rule) => {
        if (rule.type === "AND" || rule.type === "OR") {
          const nested = rule.rules.map(formatRule).join(` ${rule.type} `);
          return `(${nested})`;
        }
        return `${this.formatField(rule.field)} ${this.formatOperator(rule.operator)} "${rule.value}"`;
      };

      const rules = conditions.rules.map(formatRule);
      return rules.join(` ${conditions.type} `);
    },
    formatField(field) {
      const fields = {
        "product._id": "Product ID",
        "product.group": "Group",
        "product.supplier": "Supplier",
        "product.brand": "Brand",
        "product.cannabisGramsUnit": "Cannabis Size",
        "item.unitPrice": "Unit Price",
        "customer.isVeteran": "Is Veteran",
        "customer.visitCount": "Visit Count",
        "customer.firstPurchase": "First Purchase",
      };
      return fields[field] || field;
    },
    formatOperator(op) {
      const operators = {
        eq: "=",
        neq: "≠",
        gt: ">",
        gte: "≥",
        lt: "<",
        lte: "≤",
        in: "in",
        nin: "not in",
      };
      return operators[op] || op;
    },
  },
};
</script>

<style lang="scss" scoped>
.discount-rule-preview {
  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 2rem;
  }

  background: #1e1e20;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(96, 165, 250, 0.1);
    border-radius: 12px 12px 0 0;
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #60a5fa;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    i {
      font-size: 16px;
    }
  }

  &__close {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
  }

  &__section {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    &:last-child {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }
  }

  &__section-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      color: #60a5fa;
      font-size: 12px;
    }
  }

  &__info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    @media (max-width: 500px) {
      grid-template-columns: 1fr;
    }
  }

  &__info-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.5);
  }

  &__value {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
  }

  &__badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    background: rgba(96, 165, 250, 0.15);
    color: #60a5fa;
    width: fit-content;

    &--success {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
    }

    &--danger {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
    }
  }

  &__description {
    margin-top: 1rem;

    p {
      margin: 0.25rem 0 0 0;
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      line-height: 1.5;
    }
  }

  &__empty {
    color: rgba(255, 255, 255, 0.4);
    font-size: 13px;
    font-style: italic;
    padding: 0.5rem 0;
  }

  &__summary {
    margin: 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
    background: rgba(0, 0, 0, 0.2);
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-family: monospace;
    line-height: 1.6;
    word-break: break-word;
  }

  &__triggers {
    ul {
      margin: 0;
      padding-left: 1.25rem;

      li {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.8);
        margin-bottom: 0.5rem;

        &:last-child {
          margin-bottom: 0;
        }

        strong {
          color: #f59e0b;
        }
      }
    }
  }

  &__effects {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__effect-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 6px;
    flex-wrap: wrap;
  }

  &__effect-type {
    font-size: 12px;
    font-weight: 500;
    color: #10b981;
    background: rgba(16, 185, 129, 0.15);
    padding: 4px 8px;
    border-radius: 4px;
  }

  &__effect-value {
    font-size: 18px;
    font-weight: 700;
    color: #10b981;
  }

  &__effect-details {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    display: flex;
    gap: 1rem;
    flex: 1;
    justify-content: flex-end;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__tag {
    font-size: 12px;
    padding: 4px 10px;
    background: rgba(139, 92, 246, 0.15);
    color: #a78bfa;
    border-radius: 4px;
  }

  &__bundle-groups {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__bundle-group {
    padding: 0.75rem 1rem;
    background: rgba(168, 85, 247, 0.1);
    border: 1px solid rgba(168, 85, 247, 0.2);
    border-radius: 6px;
  }

  &__bundle-group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  &__bundle-group-label {
    font-size: 13px;
    font-weight: 600;
    color: #a855f7;
  }

  &__bundle-group-qty {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    background: rgba(168, 85, 247, 0.15);
    padding: 3px 8px;
    border-radius: 4px;
  }

  &__max-bundles {
    margin-top: 1rem;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);

    strong {
      color: #a855f7;
    }

    em {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &__footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: flex-end;
  }

  &__btn {
    padding: 10px 24px;
    font-size: 13px;
    font-weight: 500;
    background: rgba(96, 165, 250, 0.15);
    border: 1px solid rgba(96, 165, 250, 0.3);
    color: #60a5fa;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(96, 165, 250, 0.25);
    }
  }
}
</style>

